import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  DtoFormData,
  EClearCookieFormFields,
  EConfirmationDialogFields,
  EContactFormFields,
  ECvDialogFields,
  ESelfServiceFormFields,
  FormName,
  Language
} from '@common';
import { Observable, of, tap } from 'rxjs';

export type DtoClearCookieFormData = Record<EClearCookieFormFields, string | Array<string>>;
export type DtoContactFormData = Record<EContactFormFields, string | Array<string>>;
export type DtoConfirmationDialogData = Record<EConfirmationDialogFields, string | Array<string>>;
export type DtoSelfServiceFormData = Record<ESelfServiceFormFields, string | Array<string>>;
export type DtoCvDialogData = Record<ECvDialogFields, string | Array<string>>;

@Injectable({
  providedIn: 'root'
})
export class FormDataService {
  //#region Private Fields ----------------------------------------------------
  private readonly httpClient: HttpClient;
  private readonly formDataRoot: string;
  private readonly formDataCache: Map<FormName, Map<Language, DtoFormData>>;
  private _processErrorMessage: string;
  //#endregion

  //#region public properties -------------------------------------------------
  public get processErrorMessage(): string {
    return this._processErrorMessage;
  }
  //#endregion

  //#region Constructor & C° --------------------------------------------------
  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
    this._processErrorMessage = '';
    this.formDataRoot = 'api/site/forms/';
    this.formDataCache = new Map<FormName, Map<Language, DtoFormData>>([
      ['clear-cookie-form', new Map<Language, DtoFormData>()],
      ['contact-form', new Map<Language, DtoFormData>()],
      ['cv-dialog', new Map<Language, DtoFormData>()],
      ['save-cookie-dialog', new Map<Language, DtoFormData>()],
      ['self-service-form', new Map<Language, DtoFormData>()]
    ]);
  }
  //#endregion

  //#region public methods ---------------------------------------------------
  public getCookieConfirmationData(language: Language): Observable<DtoFormData> {
    return this.getOrFetch('save-cookie-dialog', language);
  }

  public getContactFormData(language: Language): Observable<DtoFormData> {
    return this.getOrFetch('contact-form', language);
  }

  public getCookieFormData(language: Language): Observable<DtoFormData> {
    return this.getOrFetch('clear-cookie-form', language);
  }

  public getCvDialogData(language: Language): Observable<DtoFormData> {
    return this.getOrFetch('cv-dialog', language);
  }

  public getSelfServiceFormData(language: Language): Observable<DtoFormData> {
    return this.getOrFetch('self-service-form', language);
  }

  //#endregion

  //#region private methods --------------------------------------------------
  private getOrFetch(formName: FormName, language: Language | null): Observable<DtoFormData> {
    if (this.formDataCache.get(formName).has(language)) {
      return of(this.formDataCache.get(formName).get(language));
    } else {
      const formDataUrl =
        language == null ? `${this.formDataRoot}${formName}` : `${this.formDataRoot}${formName}/${language}`;
      return this.httpClient
        .get<DtoFormData>(formDataUrl)
        .pipe(tap((resp: DtoFormData) => this.formDataCache.get(formName).set(language, resp)));
    }
  }
  //#endregion
}
